export const supportedLanguages = ['zh', 'en', 'ja', 'id', 'es', 'ms'] as const;

export type Language = (typeof supportedLanguages)[number];

export const languageOptions: Array<{
  code: Language;
  label: string;
  shortLabel: string;
  htmlLang: string;
  locale: string;
}> = [
  { code: 'zh', label: '中文', shortLabel: '中', htmlLang: 'zh-CN', locale: 'zh-CN' },
  { code: 'en', label: 'English', shortLabel: 'EN', htmlLang: 'en', locale: 'en-US' },
  { code: 'ja', label: '日本語', shortLabel: '日', htmlLang: 'ja', locale: 'ja-JP' },
  { code: 'id', label: 'Bahasa Indonesia', shortLabel: 'ID', htmlLang: 'id', locale: 'id-ID' },
  { code: 'es', label: 'Español', shortLabel: 'ES', htmlLang: 'es-MX', locale: 'es-MX' },
  { code: 'ms', label: 'Bahasa Melayu', shortLabel: 'MS', htmlLang: 'ms-MY', locale: 'ms-MY' }
];

export const isSupportedLanguage = (value: string | null | undefined): value is Language =>
  !!value && supportedLanguages.includes(value as Language);

export const normalizeLanguage = (value: string | null | undefined): Language => {
  const language = value?.toLowerCase() || '';

  if (language.startsWith('zh')) return 'zh';
  if (language.startsWith('ja')) return 'ja';
  if (language.startsWith('id') || language.startsWith('in')) return 'id';
  if (language.startsWith('es')) return 'es';
  if (language.startsWith('ms')) return 'ms';
  return 'en';
};

export const getLanguageLocale = (language: Language) =>
  languageOptions.find((item) => item.code === language)?.locale || 'en-US';

type TranslationValue =
  | string
  | string[]
  | Array<Record<string, string | string[]>>
  | Record<string, any>;

export type TranslationDictionary = Record<string, TranslationValue>;

export const translations: Record<Language, TranslationDictionary> = {
  zh: {
    language: {
      label: '语言'
    },
    seo: {
      homeTitle: 'SheetMate - 表格侧边栏预览插件',
      homeDescription: '使用 SheetMate 在 Google Sheets 与 Lark Sheets 侧边栏预览 Markdown、JSON、链接、图片和 LaTeX 公式。',
      loginTitle: '登录 - SheetMate',
      loginDescription: '登录您的 SheetMate 账户',
      pricingTitle: 'SheetMate 价格',
      pricingDescription: '选择适合您工作流的 SheetMate 方案，解锁高级表格预览与对比能力。',
      mySubscriptionTitle: '我的订阅 - SheetMate',
      mySubscriptionDescription: '管理您的 SheetMate 订阅',
      privacyTitle: '隐私政策 - SheetMate',
      privacyDescription: 'SheetMate 隐私政策',
      subscriptionSuccessTitle: '订阅成功 - SheetMate',
      subscriptionSuccessDescription: '您的订阅已成功开通',
      subscriptionCanceledTitle: '订阅已取消 - SheetMate',
      subscriptionCanceledDescription: '您的订阅流程已取消',
      useCasesTitle: '行业案例 - SheetMate',
      useCasesDescription: '了解 SheetMate 如何帮助不同团队提升表格工作流效率',
      useCaseFallbackTitle: '行业案例 - SheetMate',
      useCaseFallbackDescription: '了解更多 SheetMate 使用场景'
    },
    navigation: {
      home: '首页',
      features: '功能',
      faq: '常见问题',
      pricing: '价格',
      solutions: '行业案例',
      welcome: '欢迎',
      mySubscription: '我的订阅',
      signOut: '退出登录',
      signInWithGoogle: '通过 Google 登录'
    },
    footer: {
      useCases: '行业案例',
      privacy: '隐私政策',
      feedback: '反馈与建议',
      rights: '版权所有'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Google Sheets 与 Lark Sheets 侧边栏插件',
      description: '在表格侧边栏即时预览单元格里的 Markdown、JSON、链接、图片和 LaTeX 公式；需要核对时，一键切换双窗对比。',
      primaryButton: '添加到 Chrome - 免费试用',
      secondaryButton: '查看功能要点',
      proofItems: ['选中单元格即刻预览', '双窗布局对比长内容', '数据在浏览器本地处理'],
      productFrameLabel: 'SheetMate 插件界面预览',
      sheetHeaders: {
        case: '场景',
        renderAs: '预览方式',
        rawCell: '原始文本',
        copyable: '仍可复制',
        autoDetect: '自动检测'
      },
      belowFoldCue: '向下查看核心工作流'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'AI 解答',
        cell: '### 解题思路\\n将 Markdown 长回答转成文档\\n1. **识别结构**\\n2. **保留代码**',
        note: '标题、列表、代码块',
        title: '解题思路',
        description: '将单元格里的 Markdown 长回答转成清晰文档，标题、列表和代码块都能直接阅读。',
        step1Label: '识别结构：',
        step1Text: '标题、段落、列表。',
        step2Label: '保留代码：',
        step2Text: '语法高亮更适合检查。'
      },
      json: {
        rowLabel: '发布检查',
        note: '结构化树状预览'
      },
      link: {
        label: '链接',
        rowLabel: '视频资料',
        note: 'URL 转预览',
        previewTitle: '嵌入式链接预览'
      },
      latex: {
        rowLabel: '公式推导',
        note: '公式和步骤排版',
        description: '长公式和数学解释会排成可阅读的步骤，而不是挤在一个单元格里。',
        step1Label: '化简表达式：',
        step2Label: '代入单位根：',
        step3Label: '得到结论：',
        step3Text: '比较两个乘积。'
      }
    },
    features: {
      title: '把复杂单元格变成可读内容',
      eyebrow: '核心功能',
      workflowEyebrow: '使用路径',
      coverageEyebrow: '格式覆盖',
      coverageDescription: '面向真实表格里的复杂内容，而不是只显示一段纯文本。',
      steps: ['打开 Google Sheets 或 Lark Sheets。', '选中包含 Markdown、JSON、链接、图片或公式的单元格。', 'SheetMate 自动识别内容类型并在侧边栏渲染。', '需要对比时切换上下或左右分屏，再选择第二个单元格。'],
      items: [
        { title: '智能格式预览', description: '自动识别 Markdown、LaTeX、JSON、链接、图片和对话数据，并按适合阅读的方式渲染。' },
        { title: '双窗单元格对比', description: '在上下或左右分屏中锁定两个单元格，适合核对长文本、模型输出和结构化数据。' },
        { title: '占满侧边栏空间', description: '插件界面保留紧凑工具栏，把主要空间留给正文、JSON 树、图片和网页预览。' },
        { title: '本地读取与复制', description: '内容从当前浏览器标签页读取并即时显示，可复制原始单元格内容用于下一步处理。' }
      ]
    },
    pricing: {
      title: '简单透明的价格',
      subtitle: '选择最适合您的方案',
      monthly: '月付',
      yearly: '年付',
      save: '节省17%',
      mo: '月',
      yr: '年',
      free: '免费',
      mostPopular: '最受欢迎',
      limitedTime: '限时',
      lifetime: '终身',
      processing: '处理中...',
      getStarted: '开始使用',
      subscribe: '立即订阅',
      manageSubscription: '管理订阅',
      alreadySubscribed: '您已有有效订阅，正在跳转到订阅管理页面...',
      checkoutFailed: '创建结账会话失败，请稍后再试',
      invalidCheckoutUrl: '未获取到有效的结账 URL',
      missingPlan: '未找到对应的订阅计划',
      trialDescription: '零风险体验所有专业功能',
      trialReady: '您的免费试用已准备就绪。请安装 Chrome 插件后登录同一账户使用。',
      description: '选择最适合您的方案',
      plans: [
        {
          name: '一个月免费试用',
          price: '免费',
          features: ['Markdown 格式预览和渲染', 'LaTeX 数学公式支持', 'JSON 数据格式化显示', '链接预览和富文本渲染', '代码语法高亮', '表格数据格式化', '图片链接预览'],
          buttonText: '开始免费试用'
        },
        {
          name: '专业版',
          price: '$9.9/年',
          features: ['Markdown 格式预览和渲染', 'LaTeX 数学公式支持', 'JSON 数据格式化显示', '链接预览和富文本渲染', '代码语法高亮', '表格数据格式化', '图片链接预览'],
          buttonText: '立即订阅'
        },
        {
          name: '终身版',
          price: '$19.9/终身',
          features: ['Markdown 格式预览和渲染', 'LaTeX 数学公式支持', 'JSON 数据格式化显示', '链接预览和富文本渲染', '代码语法高亮', '表格数据格式化', '图片链接预览'],
          buttonText: '立即购买'
        }
      ]
    },
    faq: {
      title: '常见问题',
      items: [
        { question: '支持哪些电子表格平台？', answer: '目前支持 Google Sheets、飞书表格和 Lark Sheets。插件会在侧边栏读取当前选中单元格。' },
        { question: '支持哪些内容格式？', answer: '支持 Markdown、LaTeX 公式、JSON、链接、图片 URL 数组、对话数组和纯文本。预览会自动检测内容类型。' },
        { question: '单元格比较功能如何使用？', answer: '切换到上下或左右分屏，点击要更新的预览窗，再回到表格选择另一个单元格即可并排查看。' },
        { question: '我的数据安全吗？', answer: '是的，我们非常重视安全性。所有数据处理都在您的浏览器本地进行，我们从不存储或传输您的电子表格数据。' },
        { question: '为什么某些链接无法预览？', answer: '某些网站出于安全原因禁止在 iframe 中嵌入其内容。这由这些网站的安全策略决定，我们无法绕过；您可以点击链接在新标签页中打开。' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: '专业版',
        description: '适合个人用户和小型团队',
        features: ['包含所有免费版功能', '高级单元格对比', '自定义格式选项', '优先支持', '新功能优先体验', '无限表格', '扩展格式选项']
      },
      'pro-lifetime': {
        name: '终身版',
        description: '一次性付费，终身使用',
        features: ['包含所有专业版功能', '高级单元格对比', '自定义格式选项', '优先支持', '新功能优先体验', '无限表格', '扩展格式选项']
      }
    },
    loading: '加载中...',
    verifyingSubscription: '正在验证订阅...',
    error: '出错了',
    mySubscription: '我的订阅',
    pleaseSignIn: '请先登录',
    signInToViewSubscription: '请登录后查看您的订阅信息',
    signIn: '登录',
    noActiveSubscription: '您还没有活跃的订阅',
    subscribeToUnlockFeatures: '开始免费试用，体验所有专业功能，包括 Markdown 预览、LaTeX 公式渲染、JSON 格式化等。',
    viewPlans: '查看订阅计划',
    accountInformation: '账户信息',
    name: '用户名',
    email: '邮箱',
    unknown: '未知',
    month: '月',
    year: '年',
    currentPeriod: '当前订阅周期',
    willCancelAtPeriodEnd: '将在当前周期结束后取消',
    willAutoRenew: '将在当前周期结束后自动续订',
    subscriptionStartedOn: '订阅开始于',
    manageSubscription: '管理订阅',
    changePlan: '更改计划',
    redirecting: '正在跳转...',
    portalOpenFailed: '无法打开订阅管理门户，请稍后再试。',
    noCustomerId: '未找到客户 ID，无法管理订阅。',
    serverError: '服务器错误',
    noPortalUrl: '未获取到门户链接',
    lifetimeAccess: '终身访问权（永久有效）',
    lifetimeSubscription: '终身订阅',
    purchaseDate: '购买日期',
    oneTimePayment: '一次性付款',
    viewOtherPlans: '查看其他计划',
    startFreeTrial: '开始免费试用',
    trialEnded: '试用期已结束',
    trialEndedDescription: '您的免费试用期已结束。订阅专业版继续使用所有功能，包括 Markdown 预览、LaTeX 公式渲染、JSON 格式化等。',
    upgradeToPro: '升级到专业版',
    trialDaysLeft: '试用期还剩 {{days}} 天',
    subscriptionLoadFailed: '无法加载您的订阅信息，请稍后再试。',
    status: {
      active: '活跃',
      active_canceling: '将在期末取消',
      canceled: '已取消',
      past_due: '逾期',
      incomplete: '未完成',
      incomplete_expired: '已过期',
      trialing: '试用中',
      unpaid: '未支付',
      unknown: '未知'
    },
    planNames: {
      unknown: '未知方案',
      monthly: '专业版（月付）',
      yearly: '专业版（年付）',
      lifetime: '专业版（终身）',
      standard: '标准方案'
    },
    login: {
      title: '登录以继续',
      description: '请使用您的 Google 账号登录以继续订阅流程。我们不会在未经您许可的情况下发布任何内容。',
      button: '使用 Google 登录'
    },
    subscriptionSuccess: {
      title: '订阅成功！',
      manageBilling: '管理账单信息',
      invalidPortalUrl: '未获取到有效的管理 URL',
      portalFailed: '创建管理会话失败，请稍后再试'
    },
    subscriptionCanceled: {
      message: '订阅已取消。正在返回价格页面...'
    },
    privacy: {
      title: '隐私政策',
      lastUpdated: '最后更新日期: {{date}}'
    },
    useCases: {
      indexTitle: '真实团队如何使用 SheetMate',
      indexSubtitle: '面向财务、运营、教育、增长和数据团队的表格预览工作流。',
      ctaTitle: '立即免费体验 SheetMate',
      ctaText: '把 Markdown、JSON、链接和公式从拥挤单元格里解放出来。',
      ctaButton: '添加到 Chrome',
      backToList: '返回行业案例',
      readingTimeLabel: '阅读时间',
      keywordsPrefix: '关键词：',
      highlightsTitle: '关键亮点',
      workflowTitle: '落地步骤'
    }
  },
  en: {
    language: {
      label: 'Language'
    },
    seo: {
      homeTitle: 'SheetMate - Spreadsheet Side Panel Preview',
      homeDescription: 'Preview Markdown, JSON, links, images, and LaTeX formulas from Google Sheets and Lark Sheets cells in a focused sidebar.',
      loginTitle: 'Login - SheetMate',
      loginDescription: 'Sign in to your SheetMate account',
      pricingTitle: 'SheetMate Pricing',
      pricingDescription: 'Choose the SheetMate plan that matches your workflow. Unlock premium spreadsheet preview and comparison features.',
      mySubscriptionTitle: 'My Subscription - SheetMate',
      mySubscriptionDescription: 'Manage your SheetMate subscription',
      privacyTitle: 'Privacy Policy - SheetMate',
      privacyDescription: 'SheetMate Privacy Policy',
      subscriptionSuccessTitle: 'Subscription Success - SheetMate',
      subscriptionSuccessDescription: 'Your subscription was successful',
      subscriptionCanceledTitle: 'Subscription Canceled - SheetMate',
      subscriptionCanceledDescription: 'Your subscription flow was canceled',
      useCasesTitle: 'Use Cases - SheetMate',
      useCasesDescription: 'Discover how SheetMate helps different teams improve spreadsheet workflows',
      useCaseFallbackTitle: 'Use Case - SheetMate',
      useCaseFallbackDescription: 'Learn more about this SheetMate use case'
    },
    navigation: {
      home: 'Home',
      features: 'Features',
      faq: 'FAQ',
      pricing: 'Pricing',
      solutions: 'Use Cases',
      welcome: 'Welcome',
      mySubscription: 'My Subscription',
      signOut: 'Sign Out',
      signInWithGoogle: 'Sign in with Google'
    },
    footer: {
      useCases: 'Use Cases',
      privacy: 'Privacy Policy',
      feedback: 'Feedback & Suggestions',
      rights: 'All Rights Reserved'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Sidebar extension for Google Sheets and Lark Sheets',
      description: 'Preview Markdown, JSON, links, images, and LaTeX formulas from spreadsheet cells in a focused sidebar. Switch to split view when you need to compare two cells.',
      primaryButton: 'Add to Chrome - Free Trial',
      secondaryButton: 'Explore the workflow',
      proofItems: ['Preview the selected cell instantly', 'Compare long cells in split view', 'Process spreadsheet data locally'],
      productFrameLabel: 'SheetMate extension interface preview',
      sheetHeaders: {
        case: 'case',
        renderAs: 'render as',
        rawCell: 'raw cell',
        copyable: 'copyable',
        autoDetect: 'auto-detect'
      },
      belowFoldCue: 'Scroll for the core workflow'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'AI answer',
        cell: '### Approach\\nTurn a Markdown cell into a document\\n1. **Detect structure**\\n2. **Keep code**',
        note: 'Headings, lists, code',
        title: 'Approach',
        description: 'Turn a Markdown cell into a readable document with headings, lists, and code blocks.',
        step1Label: 'Detect structure:',
        step1Text: 'headings, paragraphs, and lists.',
        step2Label: 'Keep code:',
        step2Text: 'syntax color makes review faster.'
      },
      json: {
        rowLabel: 'Release check',
        note: 'Structured tree preview'
      },
      link: {
        label: 'Links',
        rowLabel: 'Video reference',
        note: 'URL to rich preview',
        previewTitle: 'Embedded link preview'
      },
      latex: {
        rowLabel: 'Math derivation',
        note: 'Formula and steps',
        description: 'Math-heavy cells render as readable formulas and explanation steps.',
        step1Label: 'Simplify:',
        step2Label: 'Substitute roots:',
        step3Label: 'Evaluate:',
        step3Text: 'compare both products.'
      }
    },
    features: {
      title: 'Turn complex cells into readable content',
      eyebrow: 'Core workflow',
      workflowEyebrow: 'How it works',
      coverageEyebrow: 'Format coverage',
      coverageDescription: 'Built for complex cell content in real spreadsheets, not just plain text.',
      steps: ['Open Google Sheets or Lark Sheets.', 'Select a cell with Markdown, JSON, links, images, or formulas.', 'SheetMate detects the content type and renders it in the sidebar.', 'Switch to vertical or horizontal split view to compare a second cell.'],
      items: [
        { title: 'Smart format preview', description: 'Automatically detects Markdown, LaTeX, JSON, links, images, and dialog data, then renders each format for scanning.' },
        { title: 'Split cell comparison', description: 'Lock two cells in vertical or horizontal split view to review long text, model output, and structured data side by side.' },
        { title: 'More room for reading', description: 'A compact toolbar keeps the side panel focused on rendered text, JSON trees, images, and web previews.' },
        { title: 'Local read and copy', description: 'Reads the active browser tab, updates immediately as you move across cells, and copies the original cell content when needed.' }
      ]
    },
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that best fits your needs',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save: 'Save 17%',
      mo: 'mo',
      yr: 'yr',
      free: 'Free',
      mostPopular: 'Most Popular',
      limitedTime: 'Limited Time',
      lifetime: 'lifetime',
      processing: 'Processing...',
      getStarted: 'Get Started',
      subscribe: 'Subscribe Now',
      manageSubscription: 'Manage Subscription',
      alreadySubscribed: 'You already have an active subscription. Redirecting to subscription management...',
      checkoutFailed: 'Failed to create checkout session. Please try again later.',
      invalidCheckoutUrl: 'Invalid checkout URL',
      missingPlan: 'Matching subscription plan was not found',
      trialDescription: 'Try all Pro features risk-free',
      trialReady: 'Your free trial is ready. Install the Chrome extension and sign in with the same account.',
      description: 'Choose the plan that best fits your needs',
      plans: [
        {
          name: '1-Month Free Trial',
          price: 'Free',
          features: ['Markdown preview and rendering', 'LaTeX math formula support', 'JSON data formatting', 'Link preview and rich text', 'Code syntax highlighting', 'Table data formatting', 'Image link preview'],
          buttonText: 'Start Free Trial'
        },
        {
          name: 'Pro',
          price: '$9.9/year',
          features: ['Markdown preview and rendering', 'LaTeX math formula support', 'JSON data formatting', 'Link preview and rich text', 'Code syntax highlighting', 'Table data formatting', 'Image link preview'],
          buttonText: 'Subscribe Now'
        },
        {
          name: 'Lifetime',
          price: '$19.9/lifetime',
          features: ['Markdown preview and rendering', 'LaTeX math formula support', 'JSON data formatting', 'Link preview and rich text', 'Code syntax highlighting', 'Table data formatting', 'Image link preview'],
          buttonText: 'Buy Now'
        }
      ]
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        { question: 'Which spreadsheet platforms are supported?', answer: 'SheetMate currently supports Google Sheets, Feishu Sheets, and Lark Sheets. It reads the selected cell into the browser side panel.' },
        { question: 'What content formats are supported?', answer: 'It supports Markdown, LaTeX formulas, JSON, links, image URL arrays, dialog arrays, and plain text. The preview detects the format automatically.' },
        { question: 'How does the cell comparison feature work?', answer: 'Switch to vertical or horizontal split view, click the pane you want to update, then select another cell in the spreadsheet to compare the rendered output.' },
        { question: 'Is my data secure?', answer: 'Yes, we take security seriously. All data processing happens locally in your browser, and we never store or transmit your spreadsheet data.' },
        { question: 'Why can’t some links be previewed?', answer: 'Some websites prohibit embedding their content in iframes for security reasons. This is determined by those sites, and we cannot bypass it. In these cases, open the link in a new tab.' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: 'For individuals and small teams',
        features: ['All Free features', 'Advanced cell comparison', 'Custom formatting options', 'Priority support', 'Early access to new features', 'Unlimited sheets', 'Extended formatting options']
      },
      'pro-lifetime': {
        name: 'Lifetime',
        description: 'One-time payment, lifetime access',
        features: ['All Pro features', 'Advanced cell comparison', 'Custom formatting options', 'Priority support', 'Early access to new features', 'Unlimited sheets', 'Extended formatting options']
      }
    },
    loading: 'Loading...',
    verifyingSubscription: 'Verifying subscription...',
    error: 'Error',
    mySubscription: 'My Subscription',
    pleaseSignIn: 'Please Sign In',
    signInToViewSubscription: 'Please sign in to view your subscription information',
    signIn: 'Sign In',
    noActiveSubscription: 'No Active Subscription',
    subscribeToUnlockFeatures: 'Start your free trial to experience all Pro features including Markdown preview, LaTeX formula rendering, JSON formatting, and more.',
    viewPlans: 'View Plans',
    accountInformation: 'Account Information',
    name: 'Name',
    email: 'Email',
    unknown: 'Unknown',
    month: 'month',
    year: 'year',
    currentPeriod: 'Current Period',
    willCancelAtPeriodEnd: 'Will cancel at the end of the current period',
    willAutoRenew: 'Will automatically renew after the current period',
    subscriptionStartedOn: 'Subscription started on',
    manageSubscription: 'Manage Subscription',
    changePlan: 'Change Plan',
    redirecting: 'Redirecting...',
    portalOpenFailed: 'Could not open subscription management portal, please try again later.',
    noCustomerId: 'No customer ID found, cannot manage subscription.',
    serverError: 'Server Error',
    noPortalUrl: 'No portal URL received',
    lifetimeAccess: 'Lifetime Access (Never Expires)',
    lifetimeSubscription: 'Lifetime Subscription',
    purchaseDate: 'Purchase Date',
    oneTimePayment: 'One-time Payment',
    viewOtherPlans: 'View Other Plans',
    startFreeTrial: 'Start Free Trial',
    trialEnded: 'Trial Period Ended',
    trialEndedDescription: 'Your free trial has ended. Subscribe to Pro to continue using all features including Markdown preview, LaTeX formula rendering, JSON formatting, and more.',
    upgradeToPro: 'Upgrade to Pro',
    trialDaysLeft: '{{days}} days left in trial',
    subscriptionLoadFailed: 'Unable to load your subscription information. Please try again later.',
    status: {
      active: 'Active',
      active_canceling: 'Canceling at Period End',
      canceled: 'Canceled',
      past_due: 'Past Due',
      incomplete: 'Incomplete',
      incomplete_expired: 'Expired',
      trialing: 'Trial',
      unpaid: 'Unpaid',
      unknown: 'Unknown'
    },
    planNames: {
      unknown: 'Unknown Plan',
      monthly: 'Pro (Monthly)',
      yearly: 'Pro (Yearly)',
      lifetime: 'Pro (Lifetime)',
      standard: 'Standard Plan'
    },
    login: {
      title: 'Sign in to continue',
      description: 'Please sign in with your Google account to continue the subscription process. We won’t post anything without your permission.',
      button: 'Sign in with Google'
    },
    subscriptionSuccess: {
      title: 'Subscription Successful!',
      manageBilling: 'Manage your billing information',
      invalidPortalUrl: 'Invalid portal URL',
      portalFailed: 'Failed to create portal session. Please try again later.'
    },
    subscriptionCanceled: {
      message: 'Subscription canceled. Redirecting to pricing page...'
    },
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: {{date}}'
    },
    useCases: {
      indexTitle: 'How real teams use SheetMate',
      indexSubtitle: 'Spreadsheet preview workflows for finance, operations, education, growth, and data teams.',
      ctaTitle: 'Try SheetMate for free',
      ctaText: 'Free Markdown, JSON, links, and formulas from crowded cells.',
      ctaButton: 'Add to Chrome',
      backToList: 'Back to use cases',
      readingTimeLabel: 'Reading time',
      keywordsPrefix: 'Keywords: ',
      highlightsTitle: 'Key Highlights',
      workflowTitle: 'Implementation Steps'
    }
  },
  ja: {
    language: {
      label: '言語'
    },
    seo: {
      homeTitle: 'SheetMate - スプレッドシート用サイドバープレビュー',
      homeDescription: 'Google Sheets と Lark Sheets のセルにある Markdown、JSON、リンク、画像、LaTeX 数式をサイドバーでプレビューします。',
      loginTitle: 'ログイン - SheetMate',
      loginDescription: 'SheetMate アカウントにログイン',
      pricingTitle: 'SheetMate 料金',
      pricingDescription: 'ワークフローに合った SheetMate プランを選び、表計算の高度なプレビューと比較機能を利用できます。',
      mySubscriptionTitle: 'サブスクリプション - SheetMate',
      mySubscriptionDescription: 'SheetMate のサブスクリプションを管理',
      privacyTitle: 'プライバシーポリシー - SheetMate',
      privacyDescription: 'SheetMate プライバシーポリシー',
      subscriptionSuccessTitle: 'サブスクリプション成功 - SheetMate',
      subscriptionSuccessDescription: 'サブスクリプションが正常に完了しました',
      subscriptionCanceledTitle: 'サブスクリプション取消 - SheetMate',
      subscriptionCanceledDescription: 'サブスクリプション手続きがキャンセルされました',
      useCasesTitle: '活用事例 - SheetMate',
      useCasesDescription: 'SheetMate がさまざまなチームの表計算ワークフローをどう改善するかをご覧ください',
      useCaseFallbackTitle: '活用事例 - SheetMate',
      useCaseFallbackDescription: 'SheetMate の活用方法を見る'
    },
    navigation: {
      home: 'ホーム',
      features: '機能',
      faq: 'FAQ',
      pricing: '料金',
      solutions: '活用事例',
      welcome: 'ようこそ',
      mySubscription: 'サブスクリプション',
      signOut: 'ログアウト',
      signInWithGoogle: 'Google でログイン'
    },
    footer: {
      useCases: '活用事例',
      privacy: 'プライバシーポリシー',
      feedback: 'フィードバック',
      rights: 'All Rights Reserved'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Google Sheets と Lark Sheets 向けサイドバー拡張',
      description: 'スプレッドシートのセルにある Markdown、JSON、リンク、画像、LaTeX 数式をサイドバーですぐにプレビュー。比較したいときは分割表示に切り替えられます。',
      primaryButton: 'Chrome に追加 - 無料トライアル',
      secondaryButton: 'ワークフローを見る',
      proofItems: ['選択したセルをすぐにプレビュー', '長いセルを分割表示で比較', '表データはブラウザ内で処理'],
      productFrameLabel: 'SheetMate 拡張機能の画面プレビュー',
      sheetHeaders: {
        case: 'ケース',
        renderAs: '表示形式',
        rawCell: '元のセル',
        copyable: 'コピー可',
        autoDetect: '自動検出'
      },
      belowFoldCue: '下にスクロールして主要ワークフローを見る'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'AI 回答',
        cell: '### アプローチ\\nMarkdown の長い回答を文書化\\n1. **構造を検出**\\n2. **コードを保持**',
        note: '見出し、リスト、コード',
        title: 'アプローチ',
        description: 'Markdown セルを、見出し・リスト・コードブロック付きの読みやすい文書に変換します。',
        step1Label: '構造を検出：',
        step1Text: '見出し、段落、リスト。',
        step2Label: 'コードを保持：',
        step2Text: 'シンタックスカラーで確認が速くなります。'
      },
      json: {
        rowLabel: 'リリース確認',
        note: '構造化ツリープレビュー'
      },
      link: {
        label: 'リンク',
        rowLabel: '動画資料',
        note: 'URL をリッチプレビュー',
        previewTitle: '埋め込みリンクプレビュー'
      },
      latex: {
        rowLabel: '数式導出',
        note: '数式と手順の整形',
        description: '数式の多いセルを、読みやすい数式と説明ステップとして表示します。',
        step1Label: '式を簡約：',
        step2Label: '単位根を代入：',
        step3Label: '評価：',
        step3Text: '2つの積を比較します。'
      }
    },
    features: {
      title: '複雑なセルを読みやすいコンテンツに',
      eyebrow: '主要機能',
      workflowEyebrow: '使い方',
      coverageEyebrow: '対応形式',
      coverageDescription: '単なるプレーンテキストではなく、実際のスプレッドシートにある複雑なセル内容に対応します。',
      steps: ['Google Sheets または Lark Sheets を開きます。', 'Markdown、JSON、リンク、画像、数式を含むセルを選択します。', 'SheetMate が内容タイプを自動検出してサイドバーに表示します。', '比較したいときは上下または左右の分割表示に切り替え、2つ目のセルを選択します。'],
      items: [
        { title: 'スマート形式プレビュー', description: 'Markdown、LaTeX、JSON、リンク、画像、会話データを自動検出し、確認しやすい形式で表示します。' },
        { title: 'セルの分割比較', description: '上下または左右の分割表示で 2 つのセルを固定し、長文・モデル出力・構造化データを並べて確認できます。' },
        { title: '読むための広い表示領域', description: 'コンパクトなツールバーにより、本文、JSON ツリー、画像、Web プレビューに集中できます。' },
        { title: 'ローカル読み取りとコピー', description: '現在のブラウザタブから読み取り、セル移動に合わせて即時更新。必要なときに元のセル内容をコピーできます。' }
      ]
    },
    pricing: {
      title: 'シンプルで透明な料金',
      subtitle: '用途に合うプランをお選びください',
      monthly: '月額',
      yearly: '年額',
      save: '17% お得',
      mo: '月',
      yr: '年',
      free: '無料',
      mostPopular: '人気',
      limitedTime: '期間限定',
      lifetime: '買い切り',
      processing: '処理中...',
      getStarted: '始める',
      subscribe: '購読する',
      manageSubscription: 'サブスクリプションを管理',
      alreadySubscribed: '有効なサブスクリプションがあります。管理ページへ移動します...',
      checkoutFailed: 'チェックアウトセッションを作成できませんでした。後でもう一度お試しください。',
      invalidCheckoutUrl: 'チェックアウト URL が無効です',
      missingPlan: '該当するプランが見つかりません',
      trialDescription: 'Pro 機能をリスクなく試せます',
      trialReady: '無料トライアルの準備ができました。Chrome 拡張機能をインストールし、同じアカウントでログインしてください。',
      description: '用途に合うプランをお選びください',
      plans: [
        {
          name: '1か月無料トライアル',
          price: '無料',
          features: ['Markdown プレビューとレンダリング', 'LaTeX 数式対応', 'JSON データ整形', 'リンクプレビューとリッチテキスト', 'コードのシンタックスハイライト', '表データの整形', '画像リンクプレビュー'],
          buttonText: '無料トライアルを開始'
        },
        {
          name: 'Pro',
          price: '$9.9/年',
          features: ['Markdown プレビューとレンダリング', 'LaTeX 数式対応', 'JSON データ整形', 'リンクプレビューとリッチテキスト', 'コードのシンタックスハイライト', '表データの整形', '画像リンクプレビュー'],
          buttonText: '今すぐ購読'
        },
        {
          name: 'Lifetime',
          price: '$19.9/買い切り',
          features: ['Markdown プレビューとレンダリング', 'LaTeX 数式対応', 'JSON データ整形', 'リンクプレビューとリッチテキスト', 'コードのシンタックスハイライト', '表データの整形', '画像リンクプレビュー'],
          buttonText: '購入する'
        }
      ]
    },
    faq: {
      title: 'よくある質問',
      items: [
        { question: 'どの表計算プラットフォームに対応していますか？', answer: '現在は Google Sheets、Feishu Sheets、Lark Sheets に対応しています。選択中のセルをブラウザのサイドパネルに読み込みます。' },
        { question: 'どのコンテンツ形式に対応していますか？', answer: 'Markdown、LaTeX 数式、JSON、リンク、画像 URL 配列、会話配列、プレーンテキストに対応し、形式を自動検出します。' },
        { question: 'セル比較機能はどう使いますか？', answer: '上下または左右の分割表示に切り替え、更新したいペインをクリックしてから別のセルを選択すると、レンダリング結果を並べて比較できます。' },
        { question: 'データは安全ですか？', answer: 'はい。すべての処理はブラウザ内で行われ、スプレッドシートのデータを保存または送信することはありません。' },
        { question: '一部のリンクをプレビューできないのはなぜですか？', answer: '一部のサイトはセキュリティポリシーにより iframe 埋め込みを禁止しています。その場合は新しいタブでリンクを開いてください。' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: '個人と小規模チーム向け',
        features: ['無料版のすべての機能', '高度なセル比較', 'カスタム書式オプション', '優先サポート', '新機能の先行利用', '無制限のシート', '拡張書式オプション']
      },
      'pro-lifetime': {
        name: 'Lifetime',
        description: '一度の支払いで永久利用',
        features: ['Pro のすべての機能', '高度なセル比較', 'カスタム書式オプション', '優先サポート', '新機能の先行利用', '無制限のシート', '拡張書式オプション']
      }
    },
    loading: '読み込み中...',
    verifyingSubscription: 'サブスクリプションを確認中...',
    error: 'エラー',
    mySubscription: 'サブスクリプション',
    pleaseSignIn: 'ログインしてください',
    signInToViewSubscription: 'サブスクリプション情報を表示するにはログインしてください',
    signIn: 'ログイン',
    noActiveSubscription: '有効なサブスクリプションはありません',
    subscribeToUnlockFeatures: '無料トライアルで Markdown プレビュー、LaTeX 数式レンダリング、JSON 整形などすべての Pro 機能をお試しください。',
    viewPlans: 'プランを見る',
    accountInformation: 'アカウント情報',
    name: '名前',
    email: 'メール',
    unknown: '不明',
    month: '月',
    year: '年',
    currentPeriod: '現在の期間',
    willCancelAtPeriodEnd: '現在の期間終了時にキャンセルされます',
    willAutoRenew: '現在の期間終了後に自動更新されます',
    subscriptionStartedOn: '開始日',
    manageSubscription: 'サブスクリプションを管理',
    changePlan: 'プランを変更',
    redirecting: 'リダイレクト中...',
    portalOpenFailed: '管理ポータルを開けませんでした。後でもう一度お試しください。',
    noCustomerId: '顧客 ID が見つからないため管理できません。',
    serverError: 'サーバーエラー',
    noPortalUrl: 'ポータル URL を受信できませんでした',
    lifetimeAccess: '永久アクセス（期限なし）',
    lifetimeSubscription: '永久サブスクリプション',
    purchaseDate: '購入日',
    oneTimePayment: '一括払い',
    viewOtherPlans: '他のプランを見る',
    startFreeTrial: '無料トライアルを開始',
    trialEnded: 'トライアル期間が終了しました',
    trialEndedDescription: '無料トライアルは終了しました。Markdown プレビュー、LaTeX 数式レンダリング、JSON 整形などを継続して使うには Pro を購読してください。',
    upgradeToPro: 'Pro にアップグレード',
    trialDaysLeft: 'トライアル残り {{days}} 日',
    subscriptionLoadFailed: 'サブスクリプション情報を読み込めませんでした。後でもう一度お試しください。',
    status: {
      active: '有効',
      active_canceling: '期間終了時にキャンセル',
      canceled: 'キャンセル済み',
      past_due: '支払い期限超過',
      incomplete: '未完了',
      incomplete_expired: '期限切れ',
      trialing: 'トライアル中',
      unpaid: '未払い',
      unknown: '不明'
    },
    planNames: {
      unknown: '不明なプラン',
      monthly: 'Pro（月額）',
      yearly: 'Pro（年額）',
      lifetime: 'Pro（永久）',
      standard: '標準プラン'
    },
    login: {
      title: '続行するにはログイン',
      description: 'サブスクリプション手続きを続けるには Google アカウントでログインしてください。許可なく投稿することはありません。',
      button: 'Google でログイン'
    },
    subscriptionSuccess: {
      title: 'サブスクリプションが完了しました！',
      manageBilling: '請求情報を管理',
      invalidPortalUrl: '管理 URL が無効です',
      portalFailed: '管理セッションを作成できませんでした。後でもう一度お試しください。'
    },
    subscriptionCanceled: {
      message: 'サブスクリプションがキャンセルされました。料金ページに戻ります...'
    },
    privacy: {
      title: 'プライバシーポリシー',
      lastUpdated: '最終更新日: {{date}}'
    },
    useCases: {
      indexTitle: '実際のチームによる SheetMate 活用',
      indexSubtitle: '財務、オペレーション、教育、成長、データチーム向けの表計算プレビューワークフロー。',
      ctaTitle: 'SheetMate を無料で試す',
      ctaText: '混み合ったセルから Markdown、JSON、リンク、数式を読みやすく表示します。',
      ctaButton: 'Chrome に追加',
      backToList: '活用事例一覧へ戻る',
      readingTimeLabel: '読了時間',
      keywordsPrefix: 'キーワード：',
      highlightsTitle: '主なポイント',
      workflowTitle: '導入ステップ'
    }
  },
  id: {
    language: {
      label: 'Bahasa'
    },
    seo: {
      homeTitle: 'SheetMate - Pratinjau Sidebar untuk Spreadsheet',
      homeDescription: 'Pratinjau Markdown, JSON, tautan, gambar, dan rumus LaTeX dari sel Google Sheets dan Lark Sheets di sidebar yang fokus.',
      loginTitle: 'Masuk - SheetMate',
      loginDescription: 'Masuk ke akun SheetMate Anda',
      pricingTitle: 'Harga SheetMate',
      pricingDescription: 'Pilih paket SheetMate yang sesuai dengan alur kerja Anda dan buka fitur pratinjau serta perbandingan spreadsheet premium.',
      mySubscriptionTitle: 'Langganan Saya - SheetMate',
      mySubscriptionDescription: 'Kelola langganan SheetMate Anda',
      privacyTitle: 'Kebijakan Privasi - SheetMate',
      privacyDescription: 'Kebijakan Privasi SheetMate',
      subscriptionSuccessTitle: 'Langganan Berhasil - SheetMate',
      subscriptionSuccessDescription: 'Langganan Anda berhasil',
      subscriptionCanceledTitle: 'Langganan Dibatalkan - SheetMate',
      subscriptionCanceledDescription: 'Proses langganan Anda dibatalkan',
      useCasesTitle: 'Use Case - SheetMate',
      useCasesDescription: 'Lihat bagaimana SheetMate membantu berbagai tim meningkatkan alur kerja spreadsheet',
      useCaseFallbackTitle: 'Use Case - SheetMate',
      useCaseFallbackDescription: 'Pelajari use case SheetMate ini'
    },
    navigation: {
      home: 'Beranda',
      features: 'Fitur',
      faq: 'FAQ',
      pricing: 'Harga',
      solutions: 'Use Case',
      welcome: 'Selamat datang',
      mySubscription: 'Langganan Saya',
      signOut: 'Keluar',
      signInWithGoogle: 'Masuk dengan Google'
    },
    footer: {
      useCases: 'Use Case',
      privacy: 'Kebijakan Privasi',
      feedback: 'Masukan & Saran',
      rights: 'Hak Cipta Dilindungi'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Ekstensi sidebar untuk Google Sheets dan Lark Sheets',
      description: 'Pratinjau Markdown, JSON, tautan, gambar, dan rumus LaTeX dari sel spreadsheet di sidebar. Beralih ke tampilan split saat perlu membandingkan dua sel.',
      primaryButton: 'Tambahkan ke Chrome - Uji Coba Gratis',
      secondaryButton: 'Lihat alur kerja',
      proofItems: ['Pratinjau sel terpilih seketika', 'Bandingkan sel panjang dalam split view', 'Data spreadsheet diproses secara lokal'],
      productFrameLabel: 'Pratinjau antarmuka ekstensi SheetMate',
      sheetHeaders: {
        case: 'kasus',
        renderAs: 'tampil sebagai',
        rawCell: 'sel asli',
        copyable: 'bisa disalin',
        autoDetect: 'deteksi otomatis'
      },
      belowFoldCue: 'Gulir untuk melihat alur kerja utama'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'Jawaban AI',
        cell: '### Pendekatan\\nUbah sel Markdown panjang menjadi dokumen\\n1. **Deteksi struktur**\\n2. **Pertahankan kode**',
        note: 'Judul, daftar, kode',
        title: 'Pendekatan',
        description: 'Ubah sel Markdown menjadi dokumen yang mudah dibaca dengan judul, daftar, dan blok kode.',
        step1Label: 'Deteksi struktur:',
        step1Text: 'judul, paragraf, dan daftar.',
        step2Label: 'Pertahankan kode:',
        step2Text: 'warna sintaks membuat review lebih cepat.'
      },
      json: {
        rowLabel: 'Cek rilis',
        note: 'Pratinjau pohon terstruktur'
      },
      link: {
        label: 'Tautan',
        rowLabel: 'Referensi video',
        note: 'URL ke pratinjau kaya',
        previewTitle: 'Pratinjau tautan tertanam'
      },
      latex: {
        rowLabel: 'Turunan matematika',
        note: 'Rumus dan langkah',
        description: 'Sel yang penuh rumus ditampilkan sebagai formula dan langkah penjelasan yang mudah dibaca.',
        step1Label: 'Sederhanakan:',
        step2Label: 'Substitusi akar:',
        step3Label: 'Evaluasi:',
        step3Text: 'bandingkan kedua hasil perkalian.'
      }
    },
    features: {
      title: 'Ubah sel kompleks menjadi konten yang mudah dibaca',
      eyebrow: 'Alur kerja inti',
      workflowEyebrow: 'Cara kerja',
      coverageEyebrow: 'Format yang didukung',
      coverageDescription: 'Dibuat untuk konten sel kompleks di spreadsheet nyata, bukan hanya teks biasa.',
      steps: ['Buka Google Sheets atau Lark Sheets.', 'Pilih sel berisi Markdown, JSON, tautan, gambar, atau rumus.', 'SheetMate mendeteksi tipe konten dan merendernya di sidebar.', 'Beralih ke split view vertikal atau horizontal untuk membandingkan sel kedua.'],
      items: [
        { title: 'Pratinjau format cerdas', description: 'Mendeteksi Markdown, LaTeX, JSON, tautan, gambar, dan data dialog secara otomatis, lalu menampilkannya agar mudah dipindai.' },
        { title: 'Perbandingan sel split', description: 'Kunci dua sel dalam tampilan split vertikal atau horizontal untuk meninjau teks panjang, output model, dan data terstruktur berdampingan.' },
        { title: 'Ruang baca lebih luas', description: 'Toolbar ringkas menjaga panel samping tetap fokus pada teks, pohon JSON, gambar, dan pratinjau web.' },
        { title: 'Baca dan salin secara lokal', description: 'Membaca tab browser aktif, memperbarui saat Anda berpindah sel, dan menyalin konten sel asli saat diperlukan.' }
      ]
    },
    pricing: {
      title: 'Harga sederhana dan transparan',
      subtitle: 'Pilih paket yang paling sesuai',
      monthly: 'Bulanan',
      yearly: 'Tahunan',
      save: 'Hemat 17%',
      mo: 'bln',
      yr: 'thn',
      free: 'Gratis',
      mostPopular: 'Paling Populer',
      limitedTime: 'Terbatas',
      lifetime: 'seumur hidup',
      processing: 'Memproses...',
      getStarted: 'Mulai',
      subscribe: 'Berlangganan',
      manageSubscription: 'Kelola Langganan',
      alreadySubscribed: 'Anda sudah memiliki langganan aktif. Mengalihkan ke halaman pengelolaan...',
      checkoutFailed: 'Gagal membuat sesi checkout. Coba lagi nanti.',
      invalidCheckoutUrl: 'URL checkout tidak valid',
      missingPlan: 'Paket langganan yang sesuai tidak ditemukan',
      trialDescription: 'Coba semua fitur Pro tanpa risiko',
      trialReady: 'Uji coba gratis Anda siap. Instal ekstensi Chrome dan masuk dengan akun yang sama.',
      description: 'Pilih paket yang paling sesuai',
      plans: [
        {
          name: 'Uji Coba Gratis 1 Bulan',
          price: 'Gratis',
          features: ['Pratinjau dan rendering Markdown', 'Dukungan rumus matematika LaTeX', 'Pemformatan data JSON', 'Pratinjau tautan dan rich text', 'Highlight sintaks kode', 'Pemformatan data tabel', 'Pratinjau tautan gambar'],
          buttonText: 'Mulai Uji Coba Gratis'
        },
        {
          name: 'Pro',
          price: '$9.9/tahun',
          features: ['Pratinjau dan rendering Markdown', 'Dukungan rumus matematika LaTeX', 'Pemformatan data JSON', 'Pratinjau tautan dan rich text', 'Highlight sintaks kode', 'Pemformatan data tabel', 'Pratinjau tautan gambar'],
          buttonText: 'Berlangganan Sekarang'
        },
        {
          name: 'Lifetime',
          price: '$19.9/seumur hidup',
          features: ['Pratinjau dan rendering Markdown', 'Dukungan rumus matematika LaTeX', 'Pemformatan data JSON', 'Pratinjau tautan dan rich text', 'Highlight sintaks kode', 'Pemformatan data tabel', 'Pratinjau tautan gambar'],
          buttonText: 'Beli Sekarang'
        }
      ]
    },
    faq: {
      title: 'Pertanyaan Umum',
      items: [
        { question: 'Platform spreadsheet apa yang didukung?', answer: 'SheetMate saat ini mendukung Google Sheets, Feishu Sheets, dan Lark Sheets. Ekstensi membaca sel yang dipilih ke panel samping browser.' },
        { question: 'Format konten apa saja yang didukung?', answer: 'Mendukung Markdown, rumus LaTeX, JSON, tautan, array URL gambar, array dialog, dan teks biasa. Format akan dideteksi otomatis.' },
        { question: 'Bagaimana cara kerja perbandingan sel?', answer: 'Beralih ke split vertikal atau horizontal, klik panel yang ingin diperbarui, lalu pilih sel lain di spreadsheet untuk membandingkan hasil render.' },
        { question: 'Apakah data saya aman?', answer: 'Ya. Semua pemrosesan terjadi secara lokal di browser Anda, dan kami tidak menyimpan atau mengirim data spreadsheet Anda.' },
        { question: 'Mengapa beberapa tautan tidak bisa dipratinjau?', answer: 'Beberapa situs melarang kontennya ditanam dalam iframe karena kebijakan keamanan. Jika terjadi, buka tautan tersebut di tab baru.' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: 'Untuk individu dan tim kecil',
        features: ['Semua fitur Gratis', 'Perbandingan sel lanjutan', 'Opsi format kustom', 'Dukungan prioritas', 'Akses awal fitur baru', 'Sheet tanpa batas', 'Opsi format tambahan']
      },
      'pro-lifetime': {
        name: 'Lifetime',
        description: 'Bayar sekali, akses seumur hidup',
        features: ['Semua fitur Pro', 'Perbandingan sel lanjutan', 'Opsi format kustom', 'Dukungan prioritas', 'Akses awal fitur baru', 'Sheet tanpa batas', 'Opsi format tambahan']
      }
    },
    loading: 'Memuat...',
    verifyingSubscription: 'Memverifikasi langganan...',
    error: 'Terjadi kesalahan',
    mySubscription: 'Langganan Saya',
    pleaseSignIn: 'Silakan masuk',
    signInToViewSubscription: 'Masuk untuk melihat informasi langganan Anda',
    signIn: 'Masuk',
    noActiveSubscription: 'Tidak ada langganan aktif',
    subscribeToUnlockFeatures: 'Mulai uji coba gratis untuk mencoba semua fitur Pro termasuk pratinjau Markdown, rendering rumus LaTeX, pemformatan JSON, dan lainnya.',
    viewPlans: 'Lihat Paket',
    accountInformation: 'Informasi Akun',
    name: 'Nama',
    email: 'Email',
    unknown: 'Tidak diketahui',
    month: 'bulan',
    year: 'tahun',
    currentPeriod: 'Periode Saat Ini',
    willCancelAtPeriodEnd: 'Akan dibatalkan pada akhir periode saat ini',
    willAutoRenew: 'Akan diperpanjang otomatis setelah periode saat ini',
    subscriptionStartedOn: 'Langganan dimulai pada',
    manageSubscription: 'Kelola Langganan',
    changePlan: 'Ubah Paket',
    redirecting: 'Mengalihkan...',
    portalOpenFailed: 'Tidak dapat membuka portal pengelolaan langganan. Coba lagi nanti.',
    noCustomerId: 'ID pelanggan tidak ditemukan, langganan tidak dapat dikelola.',
    serverError: 'Kesalahan Server',
    noPortalUrl: 'URL portal tidak diterima',
    lifetimeAccess: 'Akses Seumur Hidup (Tidak Kedaluwarsa)',
    lifetimeSubscription: 'Langganan Seumur Hidup',
    purchaseDate: 'Tanggal Pembelian',
    oneTimePayment: 'Pembayaran satu kali',
    viewOtherPlans: 'Lihat Paket Lain',
    startFreeTrial: 'Mulai Uji Coba Gratis',
    trialEnded: 'Masa Uji Coba Berakhir',
    trialEndedDescription: 'Masa uji coba gratis Anda telah berakhir. Berlangganan Pro untuk terus menggunakan semua fitur termasuk pratinjau Markdown, rendering rumus LaTeX, pemformatan JSON, dan lainnya.',
    upgradeToPro: 'Upgrade ke Pro',
    trialDaysLeft: '{{days}} hari tersisa dalam uji coba',
    subscriptionLoadFailed: 'Tidak dapat memuat informasi langganan Anda. Coba lagi nanti.',
    status: {
      active: 'Aktif',
      active_canceling: 'Dibatalkan di Akhir Periode',
      canceled: 'Dibatalkan',
      past_due: 'Jatuh Tempo',
      incomplete: 'Belum Lengkap',
      incomplete_expired: 'Kedaluwarsa',
      trialing: 'Uji coba',
      unpaid: 'Belum dibayar',
      unknown: 'Tidak diketahui'
    },
    planNames: {
      unknown: 'Paket Tidak Diketahui',
      monthly: 'Pro (Bulanan)',
      yearly: 'Pro (Tahunan)',
      lifetime: 'Pro (Seumur Hidup)',
      standard: 'Paket Standar'
    },
    login: {
      title: 'Masuk untuk melanjutkan',
      description: 'Masuk dengan akun Google Anda untuk melanjutkan proses langganan. Kami tidak akan memposting apa pun tanpa izin Anda.',
      button: 'Masuk dengan Google'
    },
    subscriptionSuccess: {
      title: 'Langganan Berhasil!',
      manageBilling: 'Kelola informasi tagihan',
      invalidPortalUrl: 'URL portal tidak valid',
      portalFailed: 'Gagal membuat sesi portal. Coba lagi nanti.'
    },
    subscriptionCanceled: {
      message: 'Langganan dibatalkan. Mengalihkan kembali ke halaman harga...'
    },
    privacy: {
      title: 'Kebijakan Privasi',
      lastUpdated: 'Terakhir diperbarui: {{date}}'
    },
    useCases: {
      indexTitle: 'Cara tim nyata menggunakan SheetMate',
      indexSubtitle: 'Alur kerja pratinjau spreadsheet untuk tim finance, operasi, pendidikan, growth, dan data.',
      ctaTitle: 'Coba SheetMate gratis',
      ctaText: 'Bebaskan Markdown, JSON, tautan, dan rumus dari sel yang penuh.',
      ctaButton: 'Tambahkan ke Chrome',
      backToList: 'Kembali ke use case',
      readingTimeLabel: 'Waktu baca',
      keywordsPrefix: 'Kata kunci: ',
      highlightsTitle: 'Sorotan Utama',
      workflowTitle: 'Langkah Implementasi'
    }
  },
  es: {
    language: {
      label: 'Idioma'
    },
    seo: {
      homeTitle: 'SheetMate - Vista previa en el panel lateral para hojas de cálculo',
      homeDescription: 'Previsualiza Markdown, JSON, enlaces, imágenes y fórmulas LaTeX desde celdas de Google Sheets y Lark Sheets en una barra lateral enfocada.',
      loginTitle: 'Iniciar sesión - SheetMate',
      loginDescription: 'Inicia sesión en tu cuenta de SheetMate',
      pricingTitle: 'Precios de SheetMate',
      pricingDescription: 'Elige el plan de SheetMate que se ajuste a tu flujo de trabajo y desbloquea la vista previa y la comparación premium de hojas de cálculo.',
      mySubscriptionTitle: 'Mi suscripción - SheetMate',
      mySubscriptionDescription: 'Administra tu suscripción de SheetMate',
      privacyTitle: 'Política de privacidad - SheetMate',
      privacyDescription: 'Política de privacidad de SheetMate',
      subscriptionSuccessTitle: 'Suscripción exitosa - SheetMate',
      subscriptionSuccessDescription: 'Tu suscripción se realizó correctamente',
      subscriptionCanceledTitle: 'Suscripción cancelada - SheetMate',
      subscriptionCanceledDescription: 'Tu proceso de suscripción fue cancelado',
      useCasesTitle: 'Casos de uso - SheetMate',
      useCasesDescription: 'Descubre cómo SheetMate ayuda a distintos equipos a mejorar sus flujos de trabajo con hojas de cálculo',
      useCaseFallbackTitle: 'Caso de uso - SheetMate',
      useCaseFallbackDescription: 'Conoce más sobre este caso de uso de SheetMate'
    },
    navigation: {
      home: 'Inicio',
      features: 'Funciones',
      faq: 'Preguntas frecuentes',
      pricing: 'Precios',
      solutions: 'Casos de uso',
      welcome: 'Bienvenido',
      mySubscription: 'Mi suscripción',
      signOut: 'Cerrar sesión',
      signInWithGoogle: 'Iniciar sesión con Google'
    },
    footer: {
      useCases: 'Casos de uso',
      privacy: 'Política de privacidad',
      feedback: 'Comentarios y sugerencias',
      rights: 'Todos los derechos reservados'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Extensión de barra lateral para Google Sheets y Lark Sheets',
      description: 'Previsualiza Markdown, JSON, enlaces, imágenes y fórmulas LaTeX desde las celdas de tu hoja de cálculo en una barra lateral enfocada. Cambia a vista dividida cuando necesites comparar dos celdas.',
      primaryButton: 'Agregar a Chrome - Prueba gratis',
      secondaryButton: 'Explorar el flujo de trabajo',
      proofItems: ['Previsualiza la celda seleccionada al instante', 'Compara celdas largas en vista dividida', 'Procesa los datos de la hoja localmente'],
      productFrameLabel: 'Vista previa de la interfaz de la extensión SheetMate',
      sheetHeaders: {
        case: 'caso',
        renderAs: 'mostrar como',
        rawCell: 'celda original',
        copyable: 'copiable',
        autoDetect: 'detección automática'
      },
      belowFoldCue: 'Desplázate para ver el flujo de trabajo principal'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'Respuesta de IA',
        cell: '### Enfoque\\nConvierte una celda Markdown en un documento\\n1. **Detectar estructura**\\n2. **Conservar el código**',
        note: 'Títulos, listas, código',
        title: 'Enfoque',
        description: 'Convierte una celda Markdown en un documento legible con títulos, listas y bloques de código.',
        step1Label: 'Detectar estructura:',
        step1Text: 'títulos, párrafos y listas.',
        step2Label: 'Conservar el código:',
        step2Text: 'el color de sintaxis agiliza la revisión.'
      },
      json: {
        rowLabel: 'Revisión de lanzamiento',
        note: 'Vista previa de árbol estructurado'
      },
      link: {
        label: 'Enlaces',
        rowLabel: 'Referencia en video',
        note: 'De URL a vista previa enriquecida',
        previewTitle: 'Vista previa de enlace incrustado'
      },
      latex: {
        rowLabel: 'Derivación matemática',
        note: 'Fórmulas y pasos',
        description: 'Las celdas con muchas fórmulas se muestran como fórmulas legibles y pasos de explicación.',
        step1Label: 'Simplificar:',
        step2Label: 'Sustituir raíces:',
        step3Label: 'Evaluar:',
        step3Text: 'compara ambos productos.'
      }
    },
    features: {
      title: 'Convierte celdas complejas en contenido legible',
      eyebrow: 'Flujo de trabajo principal',
      workflowEyebrow: 'Cómo funciona',
      coverageEyebrow: 'Formatos compatibles',
      coverageDescription: 'Diseñado para el contenido complejo de celdas en hojas de cálculo reales, no solo texto plano.',
      steps: ['Abre Google Sheets o Lark Sheets.', 'Selecciona una celda con Markdown, JSON, enlaces, imágenes o fórmulas.', 'SheetMate detecta el tipo de contenido y lo muestra en la barra lateral.', 'Cambia a la vista dividida vertical u horizontal para comparar una segunda celda.'],
      items: [
        { title: 'Vista previa de formato inteligente', description: 'Detecta automáticamente Markdown, LaTeX, JSON, enlaces, imágenes y datos de diálogo, y muestra cada formato para revisarlo fácilmente.' },
        { title: 'Comparación de celdas dividida', description: 'Fija dos celdas en vista dividida vertical u horizontal para revisar textos largos, salidas de modelos y datos estructurados lado a lado.' },
        { title: 'Más espacio para leer', description: 'Una barra de herramientas compacta mantiene el panel lateral centrado en el texto, los árboles JSON, las imágenes y las vistas previas web.' },
        { title: 'Lectura y copia local', description: 'Lee la pestaña activa del navegador, se actualiza al instante al moverte entre celdas y copia el contenido original de la celda cuando lo necesitas.' }
      ]
    },
    pricing: {
      title: 'Precios simples y transparentes',
      subtitle: 'Elige el plan que mejor se adapte a tus necesidades',
      monthly: 'Mensual',
      yearly: 'Anual',
      save: 'Ahorra 17%',
      mo: 'mes',
      yr: 'año',
      free: 'Gratis',
      mostPopular: 'Más popular',
      limitedTime: 'Tiempo limitado',
      lifetime: 'de por vida',
      processing: 'Procesando...',
      getStarted: 'Comenzar',
      subscribe: 'Suscribirse ahora',
      manageSubscription: 'Administrar suscripción',
      alreadySubscribed: 'Ya tienes una suscripción activa. Redirigiendo a la gestión de la suscripción...',
      checkoutFailed: 'No se pudo crear la sesión de pago. Inténtalo de nuevo más tarde.',
      invalidCheckoutUrl: 'URL de pago no válida',
      missingPlan: 'No se encontró el plan de suscripción correspondiente',
      trialDescription: 'Prueba todas las funciones Pro sin riesgo',
      trialReady: 'Tu prueba gratuita está lista. Instala la extensión de Chrome e inicia sesión con la misma cuenta.',
      description: 'Elige el plan que mejor se adapte a tus necesidades',
      plans: [
        {
          name: 'Prueba gratis de 1 mes',
          price: 'Gratis',
          features: ['Vista previa y renderizado de Markdown', 'Compatibilidad con fórmulas matemáticas LaTeX', 'Formato de datos JSON', 'Vista previa de enlaces y texto enriquecido', 'Resaltado de sintaxis de código', 'Formato de datos de tablas', 'Vista previa de enlaces de imágenes'],
          buttonText: 'Comenzar prueba gratis'
        },
        {
          name: 'Pro',
          price: '$9.9/año',
          features: ['Vista previa y renderizado de Markdown', 'Compatibilidad con fórmulas matemáticas LaTeX', 'Formato de datos JSON', 'Vista previa de enlaces y texto enriquecido', 'Resaltado de sintaxis de código', 'Formato de datos de tablas', 'Vista previa de enlaces de imágenes'],
          buttonText: 'Suscribirse ahora'
        },
        {
          name: 'De por vida',
          price: '$19.9/de por vida',
          features: ['Vista previa y renderizado de Markdown', 'Compatibilidad con fórmulas matemáticas LaTeX', 'Formato de datos JSON', 'Vista previa de enlaces y texto enriquecido', 'Resaltado de sintaxis de código', 'Formato de datos de tablas', 'Vista previa de enlaces de imágenes'],
          buttonText: 'Comprar ahora'
        }
      ]
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        { question: '¿Qué plataformas de hojas de cálculo son compatibles?', answer: 'Actualmente SheetMate es compatible con Google Sheets, Feishu Sheets y Lark Sheets. Lee la celda seleccionada en el panel lateral del navegador.' },
        { question: '¿Qué formatos de contenido son compatibles?', answer: 'Admite Markdown, fórmulas LaTeX, JSON, enlaces, arreglos de URL de imágenes, arreglos de diálogo y texto plano. La vista previa detecta el formato automáticamente.' },
        { question: '¿Cómo funciona la comparación de celdas?', answer: 'Cambia a la vista dividida vertical u horizontal, haz clic en el panel que quieras actualizar y luego selecciona otra celda en la hoja para comparar el resultado.' },
        { question: '¿Mis datos están seguros?', answer: 'Sí, nos tomamos la seguridad muy en serio. Todo el procesamiento de datos ocurre localmente en tu navegador y nunca almacenamos ni transmitimos los datos de tu hoja de cálculo.' },
        { question: '¿Por qué algunos enlaces no se pueden previsualizar?', answer: 'Algunos sitios web prohíben incrustar su contenido en iframes por motivos de seguridad. Esto lo determinan esos sitios y no podemos evitarlo; en esos casos, abre el enlace en una pestaña nueva.' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: 'Para personas y equipos pequeños',
        features: ['Todas las funciones gratuitas', 'Comparación avanzada de celdas', 'Opciones de formato personalizadas', 'Soporte prioritario', 'Acceso anticipado a nuevas funciones', 'Hojas ilimitadas', 'Opciones de formato extendidas']
      },
      'pro-lifetime': {
        name: 'De por vida',
        description: 'Pago único, acceso de por vida',
        features: ['Todas las funciones Pro', 'Comparación avanzada de celdas', 'Opciones de formato personalizadas', 'Soporte prioritario', 'Acceso anticipado a nuevas funciones', 'Hojas ilimitadas', 'Opciones de formato extendidas']
      }
    },
    loading: 'Cargando...',
    verifyingSubscription: 'Verificando la suscripción...',
    error: 'Error',
    mySubscription: 'Mi suscripción',
    pleaseSignIn: 'Inicia sesión',
    signInToViewSubscription: 'Inicia sesión para ver la información de tu suscripción',
    signIn: 'Iniciar sesión',
    noActiveSubscription: 'Sin suscripción activa',
    subscribeToUnlockFeatures: 'Comienza tu prueba gratuita para disfrutar de todas las funciones Pro, como la vista previa de Markdown, el renderizado de fórmulas LaTeX, el formato JSON y más.',
    viewPlans: 'Ver planes',
    accountInformation: 'Información de la cuenta',
    name: 'Nombre',
    email: 'Correo electrónico',
    unknown: 'Desconocido',
    month: 'mes',
    year: 'año',
    currentPeriod: 'Periodo actual',
    willCancelAtPeriodEnd: 'Se cancelará al final del periodo actual',
    willAutoRenew: 'Se renovará automáticamente después del periodo actual',
    subscriptionStartedOn: 'La suscripción comenzó el',
    manageSubscription: 'Administrar suscripción',
    changePlan: 'Cambiar de plan',
    redirecting: 'Redirigiendo...',
    portalOpenFailed: 'No se pudo abrir el portal de gestión de la suscripción. Inténtalo de nuevo más tarde.',
    noCustomerId: 'No se encontró el ID de cliente; no se puede administrar la suscripción.',
    serverError: 'Error del servidor',
    noPortalUrl: 'No se recibió la URL del portal',
    lifetimeAccess: 'Acceso de por vida (nunca caduca)',
    lifetimeSubscription: 'Suscripción de por vida',
    purchaseDate: 'Fecha de compra',
    oneTimePayment: 'Pago único',
    viewOtherPlans: 'Ver otros planes',
    startFreeTrial: 'Comenzar prueba gratis',
    trialEnded: 'La prueba terminó',
    trialEndedDescription: 'Tu prueba gratuita ha terminado. Suscríbete a Pro para seguir usando todas las funciones, como la vista previa de Markdown, el renderizado de fórmulas LaTeX, el formato JSON y más.',
    upgradeToPro: 'Actualizar a Pro',
    trialDaysLeft: 'Quedan {{days}} días de prueba',
    subscriptionLoadFailed: 'No se pudo cargar la información de tu suscripción. Inténtalo de nuevo más tarde.',
    status: {
      active: 'Activa',
      active_canceling: 'Se cancelará al final del periodo',
      canceled: 'Cancelada',
      past_due: 'Pago vencido',
      incomplete: 'Incompleta',
      incomplete_expired: 'Caducada',
      trialing: 'Prueba',
      unpaid: 'Sin pagar',
      unknown: 'Desconocido'
    },
    planNames: {
      unknown: 'Plan desconocido',
      monthly: 'Pro (mensual)',
      yearly: 'Pro (anual)',
      lifetime: 'Pro (de por vida)',
      standard: 'Plan estándar'
    },
    login: {
      title: 'Inicia sesión para continuar',
      description: 'Inicia sesión con tu cuenta de Google para continuar con el proceso de suscripción. No publicaremos nada sin tu permiso.',
      button: 'Iniciar sesión con Google'
    },
    subscriptionSuccess: {
      title: '¡Suscripción exitosa!',
      manageBilling: 'Administra tu información de facturación',
      invalidPortalUrl: 'URL del portal no válida',
      portalFailed: 'No se pudo crear la sesión del portal. Inténtalo de nuevo más tarde.'
    },
    subscriptionCanceled: {
      message: 'Suscripción cancelada. Redirigiendo a la página de precios...'
    },
    privacy: {
      title: 'Política de privacidad',
      lastUpdated: 'Última actualización: {{date}}'
    },
    useCases: {
      indexTitle: 'Cómo usan SheetMate los equipos reales',
      indexSubtitle: 'Flujos de trabajo de vista previa de hojas de cálculo para equipos de finanzas, operaciones, educación, crecimiento y datos.',
      ctaTitle: 'Prueba SheetMate gratis',
      ctaText: 'Libera Markdown, JSON, enlaces y fórmulas de las celdas saturadas.',
      ctaButton: 'Agregar a Chrome',
      backToList: 'Volver a los casos de uso',
      readingTimeLabel: 'Tiempo de lectura',
      keywordsPrefix: 'Palabras clave: ',
      highlightsTitle: 'Puntos clave',
      workflowTitle: 'Pasos de implementación'
    }
  },
  ms: {
    language: {
      label: 'Bahasa'
    },
    seo: {
      homeTitle: 'SheetMate - Pratonton Bar Sisi untuk Hamparan',
      homeDescription: 'Pratonton Markdown, JSON, pautan, imej dan rumus LaTeX daripada sel Google Sheets dan Lark Sheets di bar sisi yang fokus.',
      loginTitle: 'Log masuk - SheetMate',
      loginDescription: 'Log masuk ke akaun SheetMate anda',
      pricingTitle: 'Harga SheetMate',
      pricingDescription: 'Pilih pelan SheetMate yang sesuai dengan aliran kerja anda dan buka ciri pratonton serta perbandingan hamparan premium.',
      mySubscriptionTitle: 'Langganan Saya - SheetMate',
      mySubscriptionDescription: 'Urus langganan SheetMate anda',
      privacyTitle: 'Dasar Privasi - SheetMate',
      privacyDescription: 'Dasar Privasi SheetMate',
      subscriptionSuccessTitle: 'Langganan Berjaya - SheetMate',
      subscriptionSuccessDescription: 'Langganan anda berjaya',
      subscriptionCanceledTitle: 'Langganan Dibatalkan - SheetMate',
      subscriptionCanceledDescription: 'Proses langganan anda dibatalkan',
      useCasesTitle: 'Kes Penggunaan - SheetMate',
      useCasesDescription: 'Lihat bagaimana SheetMate membantu pelbagai pasukan menambah baik aliran kerja hamparan',
      useCaseFallbackTitle: 'Kes Penggunaan - SheetMate',
      useCaseFallbackDescription: 'Ketahui lebih lanjut tentang kes penggunaan SheetMate ini'
    },
    navigation: {
      home: 'Laman Utama',
      features: 'Ciri',
      faq: 'Soalan Lazim',
      pricing: 'Harga',
      solutions: 'Kes Penggunaan',
      welcome: 'Selamat datang',
      mySubscription: 'Langganan Saya',
      signOut: 'Log keluar',
      signInWithGoogle: 'Log masuk dengan Google'
    },
    footer: {
      useCases: 'Kes Penggunaan',
      privacy: 'Dasar Privasi',
      feedback: 'Maklum Balas & Cadangan',
      rights: 'Hak Cipta Terpelihara'
    },
    hero: {
      title: 'SheetMate',
      badge: 'Sambungan bar sisi untuk Google Sheets dan Lark Sheets',
      description: 'Pratonton Markdown, JSON, pautan, imej dan rumus LaTeX daripada sel hamparan di bar sisi. Tukar ke paparan pisah apabila anda perlu membandingkan dua sel.',
      primaryButton: 'Tambah ke Chrome - Percubaan Percuma',
      secondaryButton: 'Lihat aliran kerja',
      proofItems: ['Pratonton sel terpilih serta-merta', 'Bandingkan sel panjang dalam paparan pisah', 'Data hamparan diproses secara setempat'],
      productFrameLabel: 'Pratonton antara muka sambungan SheetMate',
      sheetHeaders: {
        case: 'kes',
        renderAs: 'papar sebagai',
        rawCell: 'sel asal',
        copyable: 'boleh disalin',
        autoDetect: 'auto-kesan'
      },
      belowFoldCue: 'Tatal untuk melihat aliran kerja utama'
    },
    heroDemo: {
      markdown: {
        rowLabel: 'Jawapan AI',
        cell: '### Pendekatan\\nTukar sel Markdown panjang menjadi dokumen\\n1. **Kesan struktur**\\n2. **Kekalkan kod**',
        note: 'Tajuk, senarai, kod',
        title: 'Pendekatan',
        description: 'Tukar sel Markdown menjadi dokumen yang mudah dibaca dengan tajuk, senarai dan blok kod.',
        step1Label: 'Kesan struktur:',
        step1Text: 'tajuk, perenggan dan senarai.',
        step2Label: 'Kekalkan kod:',
        step2Text: 'warna sintaks mempercepat semakan.'
      },
      json: {
        rowLabel: 'Semakan keluaran',
        note: 'Pratonton pokok berstruktur'
      },
      link: {
        label: 'Pautan',
        rowLabel: 'Rujukan video',
        note: 'URL kepada pratonton kaya',
        previewTitle: 'Pratonton pautan terbenam'
      },
      latex: {
        rowLabel: 'Terbitan matematik',
        note: 'Rumus dan langkah',
        description: 'Sel yang penuh rumus dipaparkan sebagai formula dan langkah penerangan yang mudah dibaca.',
        step1Label: 'Permudah:',
        step2Label: 'Gantikan punca:',
        step3Label: 'Nilai:',
        step3Text: 'bandingkan kedua-dua hasil darab.'
      }
    },
    features: {
      title: 'Tukar sel kompleks menjadi kandungan yang mudah dibaca',
      eyebrow: 'Aliran kerja teras',
      workflowEyebrow: 'Cara ia berfungsi',
      coverageEyebrow: 'Format yang disokong',
      coverageDescription: 'Dibina untuk kandungan sel kompleks dalam hamparan sebenar, bukan sekadar teks biasa.',
      steps: ['Buka Google Sheets atau Lark Sheets.', 'Pilih sel yang mengandungi Markdown, JSON, pautan, imej atau rumus.', 'SheetMate mengesan jenis kandungan dan memaparkannya di bar sisi.', 'Tukar ke paparan pisah menegak atau mendatar untuk membandingkan sel kedua.'],
      items: [
        { title: 'Pratonton format pintar', description: 'Mengesan Markdown, LaTeX, JSON, pautan, imej dan data dialog secara automatik, kemudian memaparkan setiap format supaya mudah diimbas.' },
        { title: 'Perbandingan sel terpisah', description: 'Kunci dua sel dalam paparan pisah menegak atau mendatar untuk menyemak teks panjang, output model dan data berstruktur bersebelahan.' },
        { title: 'Ruang membaca lebih luas', description: 'Bar alat ringkas memastikan panel sisi kekal fokus pada teks, pokok JSON, imej dan pratonton web.' },
        { title: 'Baca dan salin secara setempat', description: 'Membaca tab pelayar aktif, mengemas kini serta-merta apabila anda berpindah sel, dan menyalin kandungan sel asal apabila diperlukan.' }
      ]
    },
    pricing: {
      title: 'Harga ringkas dan telus',
      subtitle: 'Pilih pelan yang paling sesuai',
      monthly: 'Bulanan',
      yearly: 'Tahunan',
      save: 'Jimat 17%',
      mo: 'bln',
      yr: 'thn',
      free: 'Percuma',
      mostPopular: 'Paling Popular',
      limitedTime: 'Masa Terhad',
      lifetime: 'seumur hidup',
      processing: 'Memproses...',
      getStarted: 'Mula',
      subscribe: 'Langgan Sekarang',
      manageSubscription: 'Urus Langganan',
      alreadySubscribed: 'Anda sudah mempunyai langganan aktif. Mengubah hala ke halaman pengurusan...',
      checkoutFailed: 'Gagal membuat sesi pembayaran. Cuba lagi kemudian.',
      invalidCheckoutUrl: 'URL pembayaran tidak sah',
      missingPlan: 'Pelan langganan yang sepadan tidak dijumpai',
      trialDescription: 'Cuba semua ciri Pro tanpa risiko',
      trialReady: 'Percubaan percuma anda sudah sedia. Pasang sambungan Chrome dan log masuk dengan akaun yang sama.',
      description: 'Pilih pelan yang paling sesuai',
      plans: [
        {
          name: 'Percubaan Percuma 1 Bulan',
          price: 'Percuma',
          features: ['Pratonton dan pemaparan Markdown', 'Sokongan rumus matematik LaTeX', 'Pemformatan data JSON', 'Pratonton pautan dan teks kaya', 'Penyerlahan sintaks kod', 'Pemformatan data jadual', 'Pratonton pautan imej'],
          buttonText: 'Mulakan Percubaan Percuma'
        },
        {
          name: 'Pro',
          price: '$9.9/tahun',
          features: ['Pratonton dan pemaparan Markdown', 'Sokongan rumus matematik LaTeX', 'Pemformatan data JSON', 'Pratonton pautan dan teks kaya', 'Penyerlahan sintaks kod', 'Pemformatan data jadual', 'Pratonton pautan imej'],
          buttonText: 'Langgan Sekarang'
        },
        {
          name: 'Seumur Hidup',
          price: '$19.9/seumur hidup',
          features: ['Pratonton dan pemaparan Markdown', 'Sokongan rumus matematik LaTeX', 'Pemformatan data JSON', 'Pratonton pautan dan teks kaya', 'Penyerlahan sintaks kod', 'Pemformatan data jadual', 'Pratonton pautan imej'],
          buttonText: 'Beli Sekarang'
        }
      ]
    },
    faq: {
      title: 'Soalan Lazim',
      items: [
        { question: 'Platform hamparan manakah yang disokong?', answer: 'SheetMate kini menyokong Google Sheets, Feishu Sheets dan Lark Sheets. Ia membaca sel terpilih ke panel sisi pelayar.' },
        { question: 'Format kandungan apakah yang disokong?', answer: 'Menyokong Markdown, rumus LaTeX, JSON, pautan, tatasusunan URL imej, tatasusunan dialog dan teks biasa. Pratonton mengesan format secara automatik.' },
        { question: 'Bagaimanakah ciri perbandingan sel berfungsi?', answer: 'Tukar ke paparan pisah menegak atau mendatar, klik anak tetingkap yang ingin dikemas kini, kemudian pilih sel lain dalam hamparan untuk membandingkan hasil paparan.' },
        { question: 'Adakah data saya selamat?', answer: 'Ya, kami mengambil berat tentang keselamatan. Semua pemprosesan data berlaku secara setempat dalam pelayar anda, dan kami tidak pernah menyimpan atau menghantar data hamparan anda.' },
        { question: 'Mengapa sesetengah pautan tidak boleh dipratonton?', answer: 'Sesetengah laman web melarang kandungan mereka dibenamkan dalam iframe atas sebab keselamatan. Ini ditentukan oleh laman tersebut dan kami tidak dapat memintasnya; buka pautan itu dalam tab baharu.' }
      ]
    },
    plans: {
      'pro-yearly': {
        name: 'Pro',
        description: 'Untuk individu dan pasukan kecil',
        features: ['Semua ciri Percuma', 'Perbandingan sel lanjutan', 'Pilihan format tersuai', 'Sokongan keutamaan', 'Akses awal kepada ciri baharu', 'Hamparan tanpa had', 'Pilihan format lanjutan']
      },
      'pro-lifetime': {
        name: 'Seumur Hidup',
        description: 'Bayar sekali, akses seumur hidup',
        features: ['Semua ciri Pro', 'Perbandingan sel lanjutan', 'Pilihan format tersuai', 'Sokongan keutamaan', 'Akses awal kepada ciri baharu', 'Hamparan tanpa had', 'Pilihan format lanjutan']
      }
    },
    loading: 'Memuatkan...',
    verifyingSubscription: 'Mengesahkan langganan...',
    error: 'Ralat',
    mySubscription: 'Langganan Saya',
    pleaseSignIn: 'Sila log masuk',
    signInToViewSubscription: 'Log masuk untuk melihat maklumat langganan anda',
    signIn: 'Log masuk',
    noActiveSubscription: 'Tiada langganan aktif',
    subscribeToUnlockFeatures: 'Mulakan percubaan percuma anda untuk mencuba semua ciri Pro termasuk pratonton Markdown, pemaparan rumus LaTeX, pemformatan JSON dan banyak lagi.',
    viewPlans: 'Lihat Pelan',
    accountInformation: 'Maklumat Akaun',
    name: 'Nama',
    email: 'E-mel',
    unknown: 'Tidak diketahui',
    month: 'bulan',
    year: 'tahun',
    currentPeriod: 'Tempoh Semasa',
    willCancelAtPeriodEnd: 'Akan dibatalkan pada akhir tempoh semasa',
    willAutoRenew: 'Akan diperbaharui secara automatik selepas tempoh semasa',
    subscriptionStartedOn: 'Langganan bermula pada',
    manageSubscription: 'Urus Langganan',
    changePlan: 'Tukar Pelan',
    redirecting: 'Mengubah hala...',
    portalOpenFailed: 'Tidak dapat membuka portal pengurusan langganan. Cuba lagi kemudian.',
    noCustomerId: 'ID pelanggan tidak dijumpai, langganan tidak dapat diurus.',
    serverError: 'Ralat Pelayan',
    noPortalUrl: 'URL portal tidak diterima',
    lifetimeAccess: 'Akses Seumur Hidup (Tidak Luput)',
    lifetimeSubscription: 'Langganan Seumur Hidup',
    purchaseDate: 'Tarikh Pembelian',
    oneTimePayment: 'Pembayaran sekali sahaja',
    viewOtherPlans: 'Lihat Pelan Lain',
    startFreeTrial: 'Mulakan Percubaan Percuma',
    trialEnded: 'Tempoh Percubaan Tamat',
    trialEndedDescription: 'Tempoh percubaan percuma anda telah tamat. Langgan Pro untuk terus menggunakan semua ciri termasuk pratonton Markdown, pemaparan rumus LaTeX, pemformatan JSON dan banyak lagi.',
    upgradeToPro: 'Naik taraf ke Pro',
    trialDaysLeft: '{{days}} hari berbaki dalam percubaan',
    subscriptionLoadFailed: 'Tidak dapat memuatkan maklumat langganan anda. Cuba lagi kemudian.',
    status: {
      active: 'Aktif',
      active_canceling: 'Dibatalkan pada Akhir Tempoh',
      canceled: 'Dibatalkan',
      past_due: 'Tertunggak',
      incomplete: 'Tidak Lengkap',
      incomplete_expired: 'Luput',
      trialing: 'Percubaan',
      unpaid: 'Belum Dibayar',
      unknown: 'Tidak diketahui'
    },
    planNames: {
      unknown: 'Pelan Tidak Diketahui',
      monthly: 'Pro (Bulanan)',
      yearly: 'Pro (Tahunan)',
      lifetime: 'Pro (Seumur Hidup)',
      standard: 'Pelan Standard'
    },
    login: {
      title: 'Log masuk untuk meneruskan',
      description: 'Sila log masuk dengan akaun Google anda untuk meneruskan proses langganan. Kami tidak akan menyiarkan apa-apa tanpa kebenaran anda.',
      button: 'Log masuk dengan Google'
    },
    subscriptionSuccess: {
      title: 'Langganan Berjaya!',
      manageBilling: 'Urus maklumat pengebilan',
      invalidPortalUrl: 'URL portal tidak sah',
      portalFailed: 'Gagal membuat sesi portal. Cuba lagi kemudian.'
    },
    subscriptionCanceled: {
      message: 'Langganan dibatalkan. Mengubah hala kembali ke halaman harga...'
    },
    privacy: {
      title: 'Dasar Privasi',
      lastUpdated: 'Kemas kini terakhir: {{date}}'
    },
    useCases: {
      indexTitle: 'Cara pasukan sebenar menggunakan SheetMate',
      indexSubtitle: 'Aliran kerja pratonton hamparan untuk pasukan kewangan, operasi, pendidikan, pertumbuhan dan data.',
      ctaTitle: 'Cuba SheetMate secara percuma',
      ctaText: 'Bebaskan Markdown, JSON, pautan dan rumus daripada sel yang padat.',
      ctaButton: 'Tambah ke Chrome',
      backToList: 'Kembali ke kes penggunaan',
      readingTimeLabel: 'Masa membaca',
      keywordsPrefix: 'Kata kunci: ',
      highlightsTitle: 'Sorotan Utama',
      workflowTitle: 'Langkah Pelaksanaan'
    }
  }
};
